import { cn } from "@somethings-ui/core";
import { MonthCalendar, Skeleton } from "@somethings-ui/web";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useEngagementDay } from "@/lib/api/mentorships";
import { civilDayKeyOf } from "../../attention/attention-view";
import type { UseActionAnalyticsResult } from "../use-action-analytics";
import { HistoryEntryRow } from "./history-entry-row";
import {
  activityToolTitle,
  toHistoryEntries,
  type DayCategories,
  type DayRundown,
  type HistoryEntry,
  type MonthSummary,
} from "./history-model";
import { HistoryRetryPrompt } from "./history-retry";
import { useHistoryCalendar } from "./use-history-calendar";

const DOT_COLOR: Record<keyof DayCategories, string> = {
  activity: "bg-brand-apricot",
  content: "bg-brand-blush",
  messages: "bg-brand-forest",
  videoCall: "bg-brand-sky",
};

const LEGEND: { key: keyof DayCategories; label: string }[] = [
  { key: "activity", label: "Activity" },
  { key: "content", label: "Content" },
  { key: "messages", label: "Messages" },
  { key: "videoCall", label: "Video call" },
];

const DOT_ORDER: (keyof DayCategories)[] = [
  "activity",
  "content",
  "messages",
  "videoCall",
];

interface HistoryCalendarViewProps {
  mentorshipId: string | null;
  analytics: UseActionAnalyticsResult;
  onSelectEntry: (entry: HistoryEntry) => void;
}

export function HistoryCalendarView({
  mentorshipId,
  analytics,
  onSelectEntry,
}: HistoryCalendarViewProps) {
  const [visibleMonth, setVisibleMonth] = useState(() =>
    DateTime.now().startOf("month").toJSDate(),
  );
  const [selectedDay, setSelectedDay] = useState<Date | undefined>();

  const {
    dayCategories,
    monthSummary,
    getDayRundown,
    isLoading,
    isError,
    refetch,
  } = useHistoryCalendar({ mentorshipId, visibleMonth });

  const handleMonthChange = useCallback((month: Date) => {
    setVisibleMonth(month);
    setSelectedDay(undefined);
  }, []);

  const handleSelect = useCallback(
    (day: Date) => {
      setSelectedDay((prev) =>
        prev &&
        DateTime.fromJSDate(prev).hasSame(DateTime.fromJSDate(day), "day")
          ? undefined
          : day,
      );
      analytics.trackHistoryDaySelect();
    },
    [analytics],
  );

  const renderDayDecoration = useCallback(
    (date: Date, state: { selected: boolean }) => {
      const key = civilDayKeyOf(date);
      const categories = dayCategories[key];
      if (!categories) return null;
      return DOT_ORDER.filter((category) => categories[category]).map(
        (category) => (
          <span
            key={category}
            className={cn(
              "size-1 rounded-full",
              state.selected ? "bg-surfaces-z5" : DOT_COLOR[category],
            )}
          />
        ),
      );
    },
    [dayCategories],
  );

  const selectedDayKey = selectedDay ? civilDayKeyOf(selectedDay) : null;

  const {
    data: dayData,
    isLoading: dayLoading,
    isError: dayError,
    refetch: refetchDay,
  } = useEngagementDay(mentorshipId, selectedDayKey);

  const selectedDayEntries = useMemo(
    () => toHistoryEntries(dayData?.entities ?? []),
    [dayData],
  );

  return (
    <div className="flex flex-col gap-3 px-2 pb-4 pt-2">
      <MonthCalendar
        month={visibleMonth}
        onMonthChange={handleMonthChange}
        selected={selectedDay}
        onSelect={handleSelect}
        weekStartsOn={1}
        dimUpcoming
        renderDayDecoration={renderDayDecoration}
        footer={<CalendarLegend />}
      />

      {isLoading ? (
        <SummarySkeleton />
      ) : isError ? (
        <SummaryError onRetry={refetch} />
      ) : selectedDay && selectedDayKey ? (
        <div className="flex flex-col gap-2">
          <DayRundownCard
            day={selectedDay}
            rundown={getDayRundown(selectedDayKey)}
          />
          <DayEntriesList
            entries={selectedDayEntries}
            isLoading={dayLoading}
            isError={dayError}
            onRetry={() => void refetchDay()}
            onSelectEntry={onSelectEntry}
          />
        </div>
      ) : (
        <MonthSummaryCard month={visibleMonth} summary={monthSummary} />
      )}
    </div>
  );
}

function DayEntriesList({
  entries,
  isLoading,
  isError,
  onRetry,
  onSelectEntry,
}: {
  entries: HistoryEntry[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onSelectEntry: (entry: HistoryEntry) => void;
}) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 px-2 py-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-2 py-2">
        <HistoryRetryPrompt
          message="Couldn't load this day's activities."
          onRetry={onRetry}
        />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="px-2 py-2 font-body text-body-3 text-text-tertiary">
        No activities or content on this day.
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      {entries.map((entry) => (
        <HistoryEntryRow
          key={entry.id}
          entry={entry}
          onClick={onSelectEntry}
        />
      ))}
    </div>
  );
}

function CalendarLegend() {
  return (
    <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
      {LEGEND.map(({ key, label }) => (
        <div key={key} className="flex items-center gap-1.5">
          <span className={cn("size-1.5 rounded-full", DOT_COLOR[key])} />
          <span className="font-body text-body-4 text-text-tertiary">
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

function SummaryCard({
  eyebrow,
  children,
}: {
  eyebrow: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex w-full flex-col gap-1.5 rounded-[20px] bg-surfaces-z3 px-3.5 py-3">
      <span className="font-body text-body-4 uppercase text-text-tertiary">
        {eyebrow}
      </span>
      {children}
    </div>
  );
}

function SummaryBreakdown({
  activities,
  content,
  messages,
  videoCalls,
}: {
  activities: number;
  content: number;
  messages: number;
  videoCalls: number;
}) {
  const rows: { key: keyof DayCategories; label: string }[] = [
    { key: "activity", label: `${activities} Activities` },
    { key: "content", label: `${content} Content items` },
    { key: "messages", label: `${messages} Messages` },
    { key: "videoCall", label: `${videoCalls} Video call` },
  ];
  return (
    <div className="flex flex-col gap-3 pt-1">
      {rows.map(({ key, label }) => (
        <div key={key} className="flex items-center gap-1.5">
          <span className={cn("size-2 rounded-full", DOT_COLOR[key])} />
          <span className="font-body text-body-3 text-text-tertiary">
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

function MonthSummaryCard({
  month,
  summary,
}: {
  month: Date;
  summary: MonthSummary;
}) {
  const eyebrow = `${DateTime.fromJSDate(month).toFormat("LLLL").toUpperCase()} SUMMARY`;

  if (!summary.hasEngagement) {
    return (
      <SummaryCard eyebrow={eyebrow}>
        <p className="font-body text-body-2 text-text-primary">No engagement</p>
      </SummaryCard>
    );
  }

  if (summary.activitiesCount === 0) {
    return (
      <SummaryCard eyebrow={eyebrow}>
        <p className="font-body text-body-2 text-text-primary">
          {summary.daysEngaged} days engaged
        </p>
      </SummaryCard>
    );
  }

  const activityWord = summary.activitiesCount === 1 ? "activity" : "activities";
  const mostUsed = summary.mostUsedActivityToolId
    ? activityToolTitle(summary.mostUsedActivityToolId)
    : null;

  return (
    <SummaryCard eyebrow={eyebrow}>
      <p className="font-body text-body-2">
        <span className="text-text-primary">
          {summary.daysEngaged} days engaged and {summary.activitiesCount}{" "}
          {activityWord}
        </span>
        <span className="text-text-secondary">
          {" "}
          shared this month.{mostUsed ? " Most used " : ""}
        </span>
        {mostUsed ? (
          <span className="text-text-primary">{mostUsed}</span>
        ) : null}
      </p>
      <SummaryBreakdown
        activities={summary.activitiesCount}
        content={summary.contentCount}
        messages={summary.messagesCount}
        videoCalls={summary.videoCallCount}
      />
    </SummaryCard>
  );
}

function DayRundownCard({
  day,
  rundown,
}: {
  day: Date;
  rundown: DayRundown;
}) {
  const eyebrow = DateTime.fromJSDate(day).toFormat("cccc, LLLL d");

  if (!rundown.hasEngagement) {
    return (
      <SummaryCard eyebrow={eyebrow}>
        <p className="font-body text-body-2 text-text-primary">No engagement</p>
      </SummaryCard>
    );
  }

  return (
    <SummaryCard eyebrow={eyebrow}>
      <p className="font-body text-body-2 text-text-primary">Daily rundown</p>
      <SummaryBreakdown
        activities={rundown.activitiesCount}
        content={rundown.contentCount}
        messages={rundown.messagesCount}
        videoCalls={rundown.videoCallCount}
      />
    </SummaryCard>
  );
}

function SummarySkeleton() {
  return (
    <div className="flex w-full flex-col gap-3 rounded-[20px] bg-surfaces-z3 px-3.5 py-3">
      <Skeleton className="h-2.5 w-1/4" />
      <Skeleton className="h-3.5 w-3/4" />
      <div className="flex flex-col gap-3 pt-1">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-3 w-2/5" />
        ))}
      </div>
    </div>
  );
}

function SummaryError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex w-full flex-col items-start gap-1 rounded-[20px] bg-surfaces-z3 px-3.5 py-3">
      <HistoryRetryPrompt message="Couldn't load this month." onRetry={onRetry} />
    </div>
  );
}
